import React, { useEffect, useState } from "react";
import AdminNav from "../../../components/nav/AdminNav";
import {
  getProductsAdmin,
  getProductsCount,
  fetchProductsByFilter,
  getProductsByCount,
  duplicateProduct,
} from "../../../functions/product";
import { updateMetaProducts } from "../../../functions/admin";
import { Link } from "react-router-dom";
import { Modal, Button, Pagination, Input, message, Dropdown, Menu } from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CopyOutlined,
  LoadingOutlined,
  SearchOutlined, SyncOutlined, MoreOutlined
} from "@ant-design/icons";
import ModalImage from "react-modal-image";
import laptop from "../../../images/laptop.png";
import { useSelector } from "react-redux";

const AllProducts = ({ history }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [productsCount, setProductsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [postPerPage] = useState(50);
  const [modalVisible, setModalVisible] = useState(false);
  const [slogan, setSlogan] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { user } = useSelector((state) => ({ ...state }));

  useEffect(() => {
    loadAllProducts();
    getProductsCount().then((res) => setProductsCount(res.data));
  }, [page]);

  const loadAllProducts = () => {
    setLoading(true);
    getProductsAdmin("createdAt", "desc", page)
      .then((res) => {
        setProducts(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleSyncProduct = async (productId) => {
    try {
      // Sync the product using the updated updateMetaProducts function
      await updateMetaProducts('CREATE', user.token, productId);
      await updateMetaProducts('UPDATE', user.token, productId);
      message.success("Product synced successfully to Meta catalogs.");
    } catch (err) {
      console.error("Failed to sync product:", err);
      message.error("Failed to sync product to Meta catalogs.");
    }
  };

  const actionMenu = (product) => (
    <Menu>
      <Menu.Item key="edit">
        <Button
          type="link"
          onClick={() => history.push(`/admin/product/${product.slug}`)}
          icon={<EditOutlined />}
          className="text-primary"
        >
          Edit
        </Button>
      </Menu.Item>
      <Menu.Item key="sync">
        <Button
          type="link"
          onClick={() => handleSyncProduct(product._id)}
          icon={<SyncOutlined />}
          className="text-success"
        >
          Sync to Meta
        </Button>
      </Menu.Item>
      <Menu.Item key="duplicate">
        <Button
          type="link"
          onClick={() => handleDuplicate(product)}
          icon={<CopyOutlined />}
          className="text-success"
        >
          Duplicate
        </Button>
      </Menu.Item>
    </Menu>
  );

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setKeyword(query);
    if (!query) {
      loadAllProducts();
    } else {
      fetchProductsByFilter({ query }).then((res) => {
        setProducts(res.data);
      });
    }
  };

  const handleDownloadProducts = () => {
    setDownloading(true);
    getProductsByCount(10000)
      .then((res) => {
        const data = res.data.map((p) => ({
          Id: p._id,
          Title: p.title,
          SKU: p.skuId,
          MRP: p.MRP,
          Price: p.price,
          Count: p.count,
          Category: p.category?.name || "N/A",
          Brand: p.brand?.name || "N/A",
        }));
        const csvData = objectToCsv(data);
        downloadCsv(csvData);
        setDownloading(false);
      })
      .catch((err) => {
        console.error(err);
        setDownloading(false);
      });
  };

  const objectToCsv = (data) => {
    const csvRows = [];
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));
    data.forEach((row) => {
      const values = headers.map((header) => `"${row[header]}"`);
      csvRows.push(values.join(","));
    });
    return csvRows.join("\n");
  };

  const downloadCsv = (data) => {
    const blob = new Blob([data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "products.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleUpdateMetaProducts = async (method) => {
    setLoading(true);
    try {
      await updateMetaProducts(method, user.token);
      message.success(
        `${method === "CREATE" ? "Created" : "Updated"} meta products successfully.`
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to update meta products.");
    }
    setLoading(false);
  };

  const handleDuplicate = (product) => {
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const handleDuplicateSubmit = () => {
    if (!slogan.trim()) {
      message.error("Slogan cannot be empty.");
      return;
    }

    const duplicateData = {
      productId: selectedProduct._id,
      slogan,
    };

    duplicateProduct(duplicateData, user.token)
      .then(() => {
        setModalVisible(false);
        setSlogan("");
        loadAllProducts();
        message.success("Product duplicated successfully!");
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to duplicate product.");
      });
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-2">
          <AdminNav />
        </div>
        <div className="col-md-10">
          <h4 className="mb-4 text-center text-primary">Manage All Products ({productsCount})</h4>

          {/* Search and Buttons */}
          <div className="row mb-4">
            <div className="col-md-4 mb-2">
              <Input
                placeholder="Search Products"
                prefix={<SearchOutlined />}
                value={keyword}
                onChange={handleSearch}
              />
            </div>
            <div className="col-md-4 mb-2">
              <Button
                type="primary"
                onClick={handleDownloadProducts}
                loading={downloading}
                icon={<SaveOutlined />}
                block
              >
                {downloading ? "Downloading..." : "Download Products"}
              </Button>
            </div>
            <div className="col-md-4 mb-2">
              <div className="btn-group w-100">
                <Button
                  type="primary"
                  onClick={() => handleUpdateMetaProducts("CREATE")}
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Create Meta Products
                </Button>
                <Button
                  type="default"
                  onClick={() => handleUpdateMetaProducts("UPDATE")}
                  loading={loading}
                  icon={<EditOutlined />}
                >
                  Update Meta Products
                </Button>
              </div>
            </div>
          </div>

          {/* Product Table */}
          <table className="table table-striped table-hover">
            <thead className="thead-dark">
              <tr>
                <th scope="col">Image</th>
                <th scope="col">Title</th>
                <th scope="col">MRP</th>
                <th scope="col">Price</th>
                <th scope="col">Count</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p._id}>
                  <td>
                    <div style={{ width: "80px", height: "auto" }}>
                      {p.images && p.images.length ? (
                        <ModalImage small={p.images[0].url} large={p.images[0].url} />
                      ) : (
                        <ModalImage small={laptop} large={laptop} />
                      )}
                    </div>
                  </td>
                  <td>
                    <Link to={`/product/${p.slug}`} className="text-decoration-none text-dark">
                      {p.title || "Untitled"}
                    </Link>
                  </td>
                  <td>₹{p.MRP || "N/A"}</td>
                  <td>₹{p.price || "N/A"}</td>
                  <td>{p.count || 0}</td>
                  <td>
                    <Dropdown overlay={actionMenu(p)} trigger={['click']}>
                      <Button icon={<MoreOutlined />}>Actions</Button>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="d-flex justify-content-center mt-4">
            <Pagination
              current={page}
              total={(productsCount / postPerPage) * 10}
              onChange={(value) => setPage(value)}
            />
          </div>

          {/* Duplicate Modal */}
          <Modal
            title="Duplicate Product"
            visible={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={[
              <Button key="cancel" onClick={() => setModalVisible(false)}>
                Cancel
              </Button>,
              <Button key="submit" type="primary" onClick={handleDuplicateSubmit}>
                Duplicate
              </Button>,
            ]}
          >
            <Input
              placeholder="Enter a slogan for the new product"
              value={slogan}
              onChange={(e) => setSlogan(e.target.value)}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default AllProducts;
